/**
 * <ec-mobile-drawer>
 *
 * Slide-in drawer with focus trap, ESC, scroll lock and ARIA dialog
 * semantics. The trigger lives outside (data-ec-drawer-trigger on the
 * hamburger inside <ec-header>); close hooks are inside the drawer
 * (data-ec-drawer-close on backdrop and X button).
 */

import { trapFocus } from '@ec/focus-trap';
import { lockScroll } from '@ec/scroll-lock';

const TRANSITION_FALLBACK_MS = 350;

class EcMobileDrawer extends HTMLElement {
  #releaseTrap = null;
  #releaseLock = null;
  #onKeydown = null;
  #onClick = null;
  #panel = null;
  #previousFocus = null;

  connectedCallback() {
    this.#panel = this.querySelector('.ec-drawer');
    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && !this.hidden) {
        event.preventDefault();
        this.close();
      }
    };
    this.#onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-ec-drawer-close]')) {
        event.preventDefault();
        this.close();
      }
    };

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.#cleanup();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  open(opts = {}) {
    if (!this.hidden) return;
    this.#previousFocus =
      opts.previousFocus instanceof HTMLElement
        ? opts.previousFocus
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    this.hidden = false;

    // Measure the announcement bar so the drawer can start below it.
    const bar = document.querySelector('.ec-announcement-bar');
    const offset = bar ? bar.getBoundingClientRect().height : 0;
    this.style.setProperty('--ec-drawer-inset-top', `${offset}px`);

    // Double rAF so the hidden→visible transition runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });
    });

    this.#releaseLock = lockScroll();
    this.#releaseTrap = trapFocus(this);

    const closeBtn = this.querySelector('.ec-drawer__close');
    if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });

    this.dispatchEvent(new CustomEvent('ec:drawer:open', { bubbles: true }));
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');

    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      this.hidden = true;
      this.#cleanup();
      if (this.#previousFocus && document.body.contains(this.#previousFocus)) {
        this.#previousFocus.focus({ preventScroll: true });
      }
      this.#previousFocus = null;
      this.dispatchEvent(new CustomEvent('ec:drawer:close', { bubbles: true }));
    };

    const onTransitionEnd = (event) => {
      if (event.target !== this.#panel) return;
      this.#panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    };

    if (this.#panel) {
      this.#panel.addEventListener('transitionend', onTransitionEnd);
      setTimeout(finish, TRANSITION_FALLBACK_MS);
    } else {
      finish();
    }
  }

  toggle() {
    if (this.hidden) this.open();
    else this.close();
  }

  #cleanup() {
    if (typeof this.#releaseTrap === 'function') {
      this.#releaseTrap();
      this.#releaseTrap = null;
    }
    if (typeof this.#releaseLock === 'function') {
      this.#releaseLock();
      this.#releaseLock = null;
    }
  }
}

if (!customElements.get('ec-mobile-drawer')) {
  customElements.define('ec-mobile-drawer', EcMobileDrawer);
}
